import { useEffect, useState } from "react";

const links = [
  { href: "#about", label: "About" },
  { href: "#experience", label: "Experience" },
  { href: "#projects", label: "Projects" },
  { href: "#skills", label: "Skills" },
  { href: "#contact", label: "Contact" },
];

const Navigation = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 48);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const sections = [
      document.querySelector("#home"),
      ...links.map((link) => document.querySelector(link.href)),
    ].filter(Boolean) as Element[];

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visible?.target.id) {
          setActiveSection(visible.target.id);
        }
      },
      { rootMargin: "-36% 0px -54% 0px", threshold: [0, 0.1, 0.35] },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    document.body.classList.add("menu-open");
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.classList.remove("menu-open");
      window.removeEventListener("keydown", handleEscape);
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <header
      className={`site-header${scrolled ? " is-scrolled" : ""}${
        menuOpen ? " menu-is-open" : ""
      }`}
    >
      <a
        className="wordmark"
        href="#home"
        aria-label="Manza — back to top"
        onClick={closeMenu}
        data-cursor="Top"
      >
        Z<span>∕</span>A
      </a>

      <nav
        className={`site-nav${menuOpen ? " is-open" : ""}`}
        id="site-navigation"
        aria-label="Primary navigation"
      >
        <div className="site-nav__mobile-meta" aria-hidden="true">
          <span>Navigate</span>
          <span>01—05</span>
        </div>
        <ul>
          {links.map((link, index) => {
            const section = link.href.slice(1);
            const isActive = activeSection === section;

            return (
              <li key={link.href}>
                <a
                  href={link.href}
                  className={isActive ? "is-active" : ""}
                  aria-current={isActive ? "location" : undefined}
                  onClick={closeMenu}
                  data-cursor="Go"
                >
                  <span className="site-nav__index">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span>{link.label}</span>
                </a>
              </li>
            );
          })}
        </ul>
        <a
          className="site-nav__email"
          href="mailto:zaiman670@gmail.com"
          onClick={closeMenu}
        >
          zaiman670@gmail.com
        </a>
      </nav>

      <button
        className="menu-toggle"
        type="button"
        aria-expanded={menuOpen}
        aria-controls="site-navigation"
        aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
        onClick={() => setMenuOpen((current) => !current)}
      >
        <span>{menuOpen ? "Close" : "Menu"}</span>
        <span className="menu-toggle__mark" aria-hidden="true">
          {menuOpen ? "×" : "+"}
        </span>
      </button>
    </header>
  );
};

export default Navigation;
